import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PACK_SIZES, fmtMoney } from "@/lib/pharmacy";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { ShieldAlert, Heart, ShieldCheck, Truck } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveImage = (u) => u ? (u.startsWith("/") ? `${BACKEND}${u}` : u) : "";

function unitPriceFor(p, pack) {
  const sale = Number(p.sale_price_per_pill || 0);
  if (sale > 0) return sale * pack;
  const raw = Number((p.price_per_pack || {})[String(pack)] || 0);
  const disc = Number(p.discount_percent || 0) / 100;
  return raw * (1 - disc);
}

function availablePacks(p) {
  const sale = Number(p.sale_price_per_pill || 0);
  if (sale > 0) return PACK_SIZES;
  return PACK_SIZES.filter((s) => (p.price_per_pack || {})[String(s)]);
}

export default function ProductDetail() {
  const { id } = useParams();
  const { addItem } = useCart();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [pack, setPack] = useState(100);
  const [qty, setQty] = useState(1);
  const [tab, setTab] = useState("overview");
  const [wished, setWished] = useState(false);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => {
      setP(r.data);
      const packs = availablePacks(r.data);
      setPack(packs.includes(100) ? 100 : (packs[0] || 100));
    });
  }, [id]);

  const toggleWish = async () => {
    if (!user) return toast.error("Sign in to save to wishlist");
    const { data } = await api.post("/wishlist/toggle", { product_id: id });
    setWished(data.added);
    toast.success(data.added ? "Added to wishlist" : "Removed");
  };

  if (!p) return <div className="blz-container py-10 text-slate-500">Loading...</div>;

  const packs = availablePacks(p);
  const unit = unitPriceFor(p, pack);
  const perPill = Number(p.sale_price_per_pill || 0) || (pack ? unit / pack : 0);
  const originalPerPill = Number(p.original_price_per_pill || 0);
  const disc = Number(p.discount_percentage || 0);

  return (
    <div className="blz-container py-8">
      <div className="text-xs text-slate-500 mb-4"><Link to="/catalogue">Catalogue</Link> / <span className="text-slate-900">{p.name}</span></div>
      <div className="grid lg:grid-cols-2 gap-10">
        <div className="rounded-3xl border border-slate-200 overflow-hidden aspect-square bg-slate-100">
          <img src={resolveImage(p.image_url)} alt={p.name} className="w-full h-full object-cover"/>
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-emerald-700">{p.category}</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mt-1" data-testid="product-name">{p.name}</h1>
          <div className="text-slate-600 mt-1">{p.generic_name}{p.strength ? ` · ${p.strength}` : ""}</div>

          <div className="flex flex-wrap gap-2 mt-3">
            {p.prescription_required ? (
              <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 text-xs font-semibold px-2.5 py-1 rounded-full border border-blue-200"><ShieldAlert className="h-3 w-3"/> Prescription required</span>
            ) : (
              <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 text-xs font-semibold px-2.5 py-1 rounded-full border border-emerald-200"><ShieldCheck className="h-3 w-3"/> Over the counter</span>
            )}
            {p.brand && <span className="text-xs text-slate-600 bg-slate-100 px-2.5 py-1 rounded-full">Brand: {p.brand}</span>}
            {p.original_packaging && <span className="text-xs text-slate-600 bg-slate-100 px-2.5 py-1 rounded-full">Original packaging: {p.original_packaging}</span>}
          </div>

          {perPill > 0 && (
            <div className="mt-4 flex items-baseline gap-3">
              <span className="text-sm text-slate-500">Per pill</span>
              <span className="font-display font-bold text-slate-900 font-mono">{fmtMoney(perPill)}</span>
              {originalPerPill > perPill && (
                <>
                  <span className="text-xs line-through text-slate-400 font-mono">{fmtMoney(originalPerPill)}</span>
                  {disc > 0 && <span className="text-xs font-semibold text-emerald-700">-{Math.round(disc)}%</span>}
                </>
              )}
            </div>
          )}

          <div className="mt-5">
            <div className="text-xs font-semibold text-slate-700 mb-2">Select pack size</div>
            <div className="flex flex-wrap gap-2" data-testid="pack-selector">
              {packs.map((s) => (
                <button key={s} onClick={() => setPack(s)} className={`pill-btn ${pack === s ? "active" : ""}`} data-testid={`pack-${s}`}>{s} pills</button>
              ))}
            </div>
            <div className="mt-2 text-xs text-slate-500 font-mono">
              {fmtMoney(perPill)}/pill × {pack} = {fmtMoney(unit)}
            </div>
          </div>

          <div className="mt-6 flex items-end gap-6">
            <div>
              {originalPerPill > perPill && <div className="text-sm line-through text-slate-400 font-mono">{fmtMoney(originalPerPill * pack)}</div>}
              <div className="font-display text-3xl font-bold text-slate-900 font-mono" data-testid="product-price">{fmtMoney(unit)}</div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-9 h-9 rounded-full border border-slate-200">-</button>
              <span className="font-mono w-6 text-center" data-testid="product-qty">{qty}</span>
              <button onClick={() => setQty(Math.min(20, qty + 1))} className="w-9 h-9 rounded-full border border-slate-200">+</button>
            </div>
          </div>

          <div className="mt-6 flex gap-3">
            <button className="btn-primary flex-1" onClick={() => { addItem(p, pack, qty); toast.success("Added to cart"); }} data-testid="pd-add-to-cart">Add to cart</button>
            <button className={`btn-ghost ${wished ? "text-red-600 border-red-200" : ""}`} onClick={toggleWish} data-testid="pd-wishlist">
              <Heart className={`h-4 w-4 ${wished ? "fill-red-600" : ""}`}/> Wishlist
            </button>
          </div>

          <div className="mt-6 grid grid-cols-2 gap-3 text-xs">
            <div className="flex items-center gap-2 rounded-xl border border-slate-200 p-3"><Truck className="h-4 w-4 text-emerald-600"/> Flat-tier shipping</div>
            <div className="flex items-center gap-2 rounded-xl border border-slate-200 p-3"><ShieldCheck className="h-4 w-4 text-emerald-600"/> Pharmacist reviewed</div>
          </div>
        </div>
      </div>

      <div className="mt-12 border-t border-slate-200 pt-8">
        <div className="flex gap-6 border-b border-slate-200">
          {["overview","dosage","storage"].map((t) => (
            <button key={t} onClick={() => setTab(t)} className={`pb-3 text-sm font-semibold capitalize ${tab === t ? "text-slate-900 border-b-2 border-emerald-600" : "text-slate-500"}`} data-testid={`tab-${t}`}>{t}</button>
          ))}
        </div>
        <div className="py-6 text-slate-700 leading-relaxed max-w-3xl">
          {tab === "overview" && <p>{p.description}</p>}
          {tab === "dosage" && <p>Follow the dosage recommended by your licensed physician. Do not exceed the maximum daily dose. Detailed dosage information is provided with the product packaging.</p>}
          {tab === "storage" && <p>Store in a cool, dry place away from direct sunlight and moisture. Keep out of reach of children. Do not use past the printed expiry date.</p>}
        </div>
      </div>
    </div>
  );
}
